import React from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { MotionHighlight } from '@/components/animate-ui'
import { 
  Menu, 
  Bell, 
  User, 
  Settings, 
  LogOut, 
  Search,
  Plus,
  HelpCircle
} from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'

interface HeaderProps {
  onMenuClick: () => void
  isMenuCollapsed: boolean
}

export default function Header({ onMenuClick, isMenuCollapsed }: HeaderProps) {
  const { user, signOut } = useAuth()
  return (
    <motion.header 
      className="bg-card border-b px-6 py-3"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <MotionHighlight>
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className="md:hidden"
              aria-label="Toggle menu"
            >
              <motion.div
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.3 }}
              >
                <Menu className="w-4 h-4" />
              </motion.div>
            </Button>
          </MotionHighlight>
          
          {/* Breadcrumb */}
          <motion.nav 
            className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <span>Dashboard</span>
            <span>/</span>
            <span className="text-foreground">Overview</span>
          </motion.nav>
        </div>

        {/* Center section - Search */}
        <motion.div 
          className="hidden md:flex items-center flex-1 max-w-md mx-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <MotionHighlight
            highlightClassName="bg-primary/5 border border-primary/20"
            className="w-full rounded-lg"
          >
            <div className="relative w-full">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ duration: 0.2 }}
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
              >
                <Search className="w-4 h-4 text-muted-foreground" />
              </motion.div>
              <input
                type="text"
                placeholder="Search galleries, services, leads..."
                className="w-full pl-10 pr-4 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors"
              />
            </div>
          </MotionHighlight>
        </motion.div>

        {/* Right section */}
        <motion.div 
          className="flex items-center gap-2"
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          {/* Quick Actions */}
          <MotionHighlight>
            <Button variant="ghost" size="icon" className="hidden md:flex" aria-label="Create new">
              <motion.div
                whileHover={{ rotate: 90, scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <Plus className="w-4 h-4" />
              </motion.div>
            </Button>
          </MotionHighlight>
          
          <MotionHighlight>
            <Button variant="ghost" size="icon" className="hidden md:flex" aria-label="Help">
              <motion.div
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <HelpCircle className="w-4 h-4" />
              </motion.div>
            </Button>
          </MotionHighlight>

          {/* Notifications */}
          <MotionHighlight>
            <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
              <motion.div
                whileHover={{ rotate: 15, scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <Bell className="w-4 h-4" />
              </motion.div>
              <motion.span 
                className="absolute -top-1 -right-1 w-3 h-3 bg-destructive rounded-full text-xs text-destructive-foreground flex items-center justify-center"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                3
              </motion.span>
            </Button>
          </MotionHighlight>

          {/* User Menu */}
          <div className="flex items-center gap-2 pl-2">
            <MotionHighlight>
              <Button variant="ghost" className="h-8 px-2 gap-2">
                <motion.div 
                  className="w-6 h-6 bg-primary rounded-full flex items-center justify-center"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <User className="w-3 h-3 text-primary-foreground" />
                </motion.div>
                <span className="hidden md:block text-sm">{user?.name || user?.email || 'User'}</span>
              </Button>
            </MotionHighlight>
            
            {/* Logout Button */}
            <MotionHighlight>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => signOut()}
                title="Sign out"
                aria-label="Sign out"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.1 }}
                >
                  <LogOut className="w-4 h-4" />
                </motion.div>
              </Button>
            </MotionHighlight>
          </div>
        </motion.div>
      </div>
    </motion.header>
  )
}